import React, { useState, Fragment, useCallback, useEffect, useRef } from 'react';
import { translate } from '../Services/translation';
import { useDispatch, useSelector } from 'react-redux';
import { reloadPayment } from '../Actions/Checkout.action';
import { update } from '../Actions/Cart.action';
import CustomBuyButton from './customBuyButton';

const mapCartToAbort = {};

const quantityDispatch = (articleNumber, quantity, dispatch) => {
    mapCartToAbort[articleNumber] = new AbortController();
    dispatch(
        update(articleNumber, quantity, mapCartToAbort[articleNumber])
    ).then(() => {
        dispatch(reloadPayment());
    });
};

const ShoppingCart = ()  => {
  const dispatch = useDispatch();
  const [removingRow, setRemovingRow] = useState({});
  const cart = useSelector((state) => state.cart);
  const [orderRows, setOrderRows] = useState(cart?.orderRows);
  useEffect(() => {
      setOrderRows(cart?.orderRows);
  }, [cart]);

  const removeOrderRow = useCallback(
      (articleNumber) =>
          dispatch(update(articleNumber, 0)).then(() =>
              dispatch(reloadPayment())
          ),
      [dispatch]
  );

  const handleQuantityInput = useCallback(
      (articleNumber, quantity, rowSystemId, ensureCorrectness = true) => {
          // In case of onBlur event, we need ensureCorrectness as true in order to ensure if the value is a valid float number
          // In case of onChange event, we accept invalid float number, but we don't send any request to the server.
          mapCartToAbort[articleNumber] &&
              mapCartToAbort[articleNumber].abort();
          let floatQuantity = parseFloat(quantity);
          let validValue = !isNaN(floatQuantity) && floatQuantity > 0;
          if (ensureCorrectness) {
              quantity = validValue ? floatQuantity : 1;
              floatQuantity = quantity;
              validValue = true;
          }
          const index = orderRows.findIndex(
              (item) => item.rowSystemId === rowSystemId
          );
          const oldQuantity = parseFloat(cart.orderRows[index].quantity);
          if (floatQuantity !== oldQuantity && validValue) {
              quantityDispatch(articleNumber, quantity, dispatch);
          }
          const tempOrderRows = [...orderRows];
          tempOrderRows[index] = {
              ...tempOrderRows[index],
              quantity,
          };
          setOrderRows(tempOrderRows);
      },
      [dispatch, orderRows, cart.orderRows]
  );

  const ProductImage = useCallback(({ order }) => {
      return (
          <img
              className="checkout-cart__image"
              src={order.image}
              alt={order.name}
          />
      );
  }, []);

  const ProductName = useCallback(({ order }) => {
      return (
          <Fragment>
              <a href={order.url} className="checkout-cart__brand-link">{order.name}</a>
              <span className="checkout-cart__brand-name" >{order.brand}</span>
              <span className="checkout-cart__brand-name">{translate('code.products.macarthur.articlenumber')} {order.articleNumber}</span>
              <span className="checkout-cart__brand-name">{translate('code.products.manufacturer.articlenumber')} : {order.manufacturerProductId}</span>
              <span className="checkout-cart__brand-name"> {translate('code.products.columnheader.manufacturer')} :  {order.manufacturerName}</span>
          </Fragment>
      );
  }, []);

  const ProductQuantity = useCallback(
      (order) => {
          return (
              <Fragment>
                  {order.isFreeGift ? (
                      <div>{order.quantity}</div>
                  ) : (
                      <input
                          className="checkout-cart__input"
                          type="number"
                          min="1"
                          maxLength={3}
                          value={order.quantity.toString()}
                          onChange={(event) =>
                              handleQuantityInput(
                                  order.articleNumber,
                                  event.target.value,
                                  order.rowSystemId,
                                  false
                              )
                          }
                          onBlur={(event) =>
                              handleQuantityInput(
                                  order.articleNumber,
                                  event.target.value,
                                  order.rowSystemId
                              )
                          }
                      />
                  )}
              </Fragment>
          );
      },
      [handleQuantityInput]
  );

  const RemoveBtn = useCallback(
      ({ order }) => {
          return (
              <Fragment>
                  {!order.isFreeGift && !removingRow[order.rowSystemId] && (
                      <a
                          className="table__icon table__icon--delete"
                          onClick={() =>
                            removeOrderRow(order.articleNumber)
                        }
                          title={translate('general.remove')}
                      ></a>
                  )}
              </Fragment>
          );
      },
      [removeOrderRow, removingRow]
  );

  useEffect(() => {
    let goBackButton = document.getElementById('back-link');

    if(goBackButton){
      goBackButton.setAttribute('href', document.referrer);
      goBackButton.onclick = function() {
        history.back();
        return false;
      }
    }

  },[])

  return (
    <div className='shopping-cart__container'>
    <h3>
      {translate('code.shopppingcart.title')}
    </h3>
    <div className='shopping-cart__content'>
            <div className='shopping-cart__items shopping-cart__items--desktop'>
            {orderRows.map((order) => (
                <div
                    className="checkout-cart__row shopping-cart__row overlay-finder"
                    key={order.rowSystemId}
                >
                    <div className="checkout-cart__image-wrapper">
                        <ProductImage order={order} />
                    </div>
                    <div className="checkout-cart__image-info">
                        <ProductName order={order} />
                    </div>
                    <div className="checkout-cart__quantity">
                        <p className="checkout-cart__measure"> {translate('code.products.unitofmeasure')} :  <span> {order.unitOfMeasurement} </span></p>
                        <CustomBuyButton articleNumber={order.articleNumber} isBuyable={true} context={'shoppingCart'} quantityIncrement={order.quantityIncrement}/>
                    </div>

                    <div className="checkout-cart__remove">
                        <RemoveBtn order={order} />
                    </div>
                </div>
            ))}
        </div>
        <div className='shopping-cart__items shopping-cart__items--mobile'>
            {orderRows.map((order) => (
                <div
                    className='shopping-cart__row shopping-cart__row--mobile overlay-finder'
                    key={order.rowSystemId}
                >
                    <div className="checkout-cart__first-section-mobile">
                        <div className="checkout-cart__image-wrapper">
                            <ProductImage order={order} />
                        </div>
                        <div className="checkout-cart__image-info">
                            <ProductName order={order} />
                        </div>
                    </div>
                    <div className="checkout-cart__second-section-mobile">
                        <div className="checkout-cart__quantity">
                            <p className="checkout-cart__measure"> {translate('code.products.unitofmeasure')} :  <span> {order.unitOfMeasurement} </span></p>
                            <CustomBuyButton articleNumber={order.articleNumber} isBuyable={true} context={'shoppingCart'} quantityIncrement={order.quantityIncrement}/>
                        </div>

                        <div className="checkout-cart__remove">
                            <RemoveBtn order={order} />
                        </div>
                    </div>
                </div>
            ))}
        </div>
        <div className='shopping-cart__summary'>
            <div className='shopping-cart__proceed'>
                <h4>{translate('code.shopppingcart.summary.title')}</h4>
                <div className='shopping-cart__number'>
                    <span>{translate('code.shopppingcart.summary.quantity')}</span>
                    <span>{cart.quantity}</span>
                </div>
                {orderRows.length > 0 ? (
                  <>
                    <a href={cart.checkoutUrl} className='shopping-cart__button button'>{translate('code.shopppingcart.summary.proceed.button')}</a>
                    <a id="back-link" className='shopping-cart__link'>{translate('code.shopppingcart.summary.link.or')}<span> {translate('code.shopppingcart.summary.link.continue')}<i className='shopping-cart__icon'></i></span> </a>
                  </>
                ):(
                  <>
                    <button className='button disabled'>{translate('code.shopppingcart.summary.noitems.button')}</button>
                  </>
                )}
            </div>
            <p>{translate('code.checkout.disclaimer.text')}</p>
        </div>
    </div>
</div>
  )
}


export default ShoppingCart